import React, { useState, useEffect, useRef } from 'react';
import _ from 'lodash';
import {
  FullWidthJustifyEndRow,
  JustifyStartRow,
} from 'spw-shared/globalComponents/grid';
import {
  numberWithCommas,
  asPercentageRounded,
  asPercentageFixed,
} from 'spw-shared/utils';
import { Box, MenuItem, Popover } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { selectCurrentUserIsViewOnly } from 'spw-redux/modules/projects/selectors';
import { GROWTH_CALCULATORS } from 'spw-constants';
import { AlignEndButtonColumn } from 'spw-components/dialogs/styles';
import { IUser } from 'spw-redux/modules/user/types';
import { saveScenarioWorkSeatsRoundingSetting } from 'spw-redux/modules/calculators/actions';
import {
  selectCurrentScenario,
  selectShowWorksheetUpdateDialog,
} from 'spw-redux/modules/calculators/selectors';
import { WorkSeatsRoundingSetting } from 'spw-redux/modules/calculators/types';
import { IProjectSpaceTypeCategory } from 'spw-redux/modules/projects/types';
import {
  GridRow,
  GridColumn,
  ProgramSubtotal,
} from 'spw-programming-projects/building-details/worksheet/styles';
import { isIWCategoryOrSubcategory } from 'spw-projects/utils';
import useUnitConversion from 'spw-components/unit-conversion/hooks';
import { ArrowDownward, ArrowUpward, Edit, Replay } from '@mui/icons-material';
import { Row } from 'spw-shared/globalComponents';
import { isWholeNumber } from 'spw-programming-projects/building-details/worksheet/utils';
import { colors } from 'spw-theme';
import { ManageSpaceTypes } from 'spw-components/worksheet/manage-space-types';
import {
  HeaderRow,
  Subtitles,
  PaddedNoOverflow,
  NoOverflow,
  EditRoundingSettingsContainer,
  WorkSeatsRoundingSettingsModalContainer,
  WorkSeatsHoverArea,
} from './styles';
import { PlainWorksheet } from '../../models/Worksheet';
import { PlainProgramScenario } from '../../models/Scenario';

interface IProps {
  color: string;
  projectSpaceTypeCategory: IProjectSpaceTypeCategory;
  totalWorkSeats?: number;
  usf?: number;
  worksheet: PlainWorksheet;
  user?: IUser;
  totalWorksheetUsf: number;
  scenario: PlainProgramScenario;
  showDecimals: boolean;
  propRef: React.RefObject<HTMLDivElement>;
  isDownload?: boolean;
  isInitiallyDisabled?: boolean;
  onManageSpaceTypesButtonClick: (
    event: React.MouseEvent<HTMLDivElement>,
    spaceTypeCategoryId: number
  ) => void;
}

const WORKSHEET_HEADER_HEIGHT = '107px';

export const SpaceTypeCategoryRow: React.FC<IProps> = ({
  projectSpaceTypeCategory,
  totalWorkSeats,
  usf,
  worksheet,
  totalWorksheetUsf,
  scenario,
  showDecimals,
  propRef,
  isDownload,
  isInitiallyDisabled,
  onManageSpaceTypesButtonClick,
}) => {
  const dispatch = useDispatch();
  const dropdownRef = useRef(null);
  const currentScenario = useSelector(selectCurrentScenario);
  const isShowingWorksheetUpdateDialog = useSelector(
    selectShowWorksheetUpdateDialog
  );
  const [disabled, setDisabled] = useState(isInitiallyDisabled);
  const currentUserIsViewOnly = useSelector(selectCurrentUserIsViewOnly);
  const { getUnit, convertToUnit } = useUnitConversion();
  const [workSeatsAreHovered, setWorkSeatsAreHovered] = useState(false);
  const [
    roundingSettingsPopupAnchorEl,
    setRoundingSettingsPopupAnchorEl,
  ] = useState<SVGSVGElement>(null);

  useEffect(() => {
    setDisabled(false);
  }, [worksheet]);

  const isExpandedScenario = !scenario.isShared;
  const isTotalProgramScenario =
    scenario.calculatorType === GROWTH_CALCULATORS.totalProgram;

  const title = projectSpaceTypeCategory.name;

  const categoryIsIndividualWorkspaces =
    projectSpaceTypeCategory.defaultSpaceTypeCategory.category ===
    'individualWorkspaces';

  let secondaryTitle = title;
  if (categoryIsIndividualWorkspaces) {
    secondaryTitle = 'Work ';
  }

  const testingSuffix = title.replace(' ', '_');

  let workseats = showDecimals
    ? totalWorkSeats.toFixed(2)
    : Math.round(totalWorkSeats);
  if (!_.isFinite(totalWorkSeats) || _.isNaN(totalWorkSeats)) {
    workseats = 0;
  }
  let { sharedSpacesAssignedSpacePercentage } = worksheet;
  if (isExpandedScenario) {
    const rawPercentage = (100 * (totalWorksheetUsf - usf)) / totalWorksheetUsf;
    sharedSpacesAssignedSpacePercentage = showDecimals
      ? rawPercentage
      : Math.round(rawPercentage);
  }
  const displaySpaceAlloc = showDecimals
    ? (100 - sharedSpacesAssignedSpacePercentage).toFixed(2)
    : Math.round(100 - sharedSpacesAssignedSpacePercentage);

  let percentProgSubUsf = showDecimals ? '0.00%' : '0%';

  if (
    !_.isNaN(usf / totalWorksheetUsf) &&
    _.isFinite(usf / totalWorksheetUsf)
  ) {
    percentProgSubUsf = showDecimals
      ? asPercentageFixed(usf / totalWorksheetUsf)
      : asPercentageRounded(usf / totalWorksheetUsf);
  }

  const updateScenarioWorkSeatsRoundingSetting = (
    workSeatsRoundingSetting: WorkSeatsRoundingSetting | null
  ) => {
    setRoundingSettingsPopupAnchorEl(null);

    dispatch(saveScenarioWorkSeatsRoundingSetting(workSeatsRoundingSetting));
  };

  const restoreRoundingSettingComponent = (
    <MenuItem onClick={() => updateScenarioWorkSeatsRoundingSetting(null)}>
      <Row alignItems="center">
        <Replay htmlColor={colors.mainBlue} fontSize="small" />
        <Box ml={0.5}>Restore</Box>
      </Row>
    </MenuItem>
  );

  const hideEditWorkSeatsButton = () => {
    setWorkSeatsAreHovered(false);
  };

  const showEditWorkSeatsButton = () => {
    setWorkSeatsAreHovered(true);
  };

  const editingWorkSeatsIsAllowed =
    categoryIsIndividualWorkspaces &&
    !isTotalProgramScenario &&
    !currentUserIsViewOnly &&
    !isWholeNumber(currentScenario.workSeatsOriginal);

  const editingWorkSeatsIsDisabled = isShowingWorksheetUpdateDialog;

  return (
    <>
      <div
        ref={propRef}
        // scroll margin top required for scroll to category header from category icon in worksheet header
        style={{
          scrollMarginTop: WORKSHEET_HEADER_HEIGHT,
        }}
      />
      <GridRow
        className="top-header"
        style={{ position: 'sticky', top: WORKSHEET_HEADER_HEIGHT, zIndex: 1 }}
      >
        <GridColumn xs={3} className="justify-start">
          <JustifyStartRow>
            <ManageSpaceTypes
              projectSpaceTypeCategory={projectSpaceTypeCategory}
              disabled={disabled || currentUserIsViewOnly}
              isTotalProgramScenario={isTotalProgramScenario}
              isDownload={isDownload}
              onManageSpaceTypesButtonClick={onManageSpaceTypesButtonClick}
            />
          </JustifyStartRow>
        </GridColumn>
        <GridColumn xs={1} />
        <GridColumn xs={3}>
          <AlignEndButtonColumn>
            <HeaderRow
              data-testid={`worksheet_category_row_value1_${testingSuffix}`}
            >
              <PaddedNoOverflow
                style={{
                  color:
                    !isWholeNumber(Number(totalWorkSeats)) &&
                    categoryIsIndividualWorkspaces
                      ? colors.red
                      : undefined,
                }}
              >
                {numberWithCommas(workseats, showDecimals, true)}
                <WorkSeatsHoverArea
                  onMouseLeave={hideEditWorkSeatsButton}
                  onMouseEnter={showEditWorkSeatsButton}
                  data-testid={
                    editingWorkSeatsIsAllowed
                      ? 'work-seats-hover-area'
                      : undefined
                  }
                >
                  {!isDownload &&
                  editingWorkSeatsIsAllowed &&
                  (!isWholeNumber(scenario.workSeats) ||
                    workSeatsAreHovered ||
                    Boolean(roundingSettingsPopupAnchorEl)) ? (
                    <EditRoundingSettingsContainer
                      style={{
                        cursor: editingWorkSeatsIsDisabled
                          ? 'default'
                          : 'pointer',
                      }}
                    >
                      <Edit
                        data-testid="edit-scenario-work-seats-button"
                        htmlColor={
                          editingWorkSeatsIsDisabled
                            ? colors.mainGray
                            : colors.mainBlue
                        }
                        onClick={(e) => {
                          if (
                            categoryIsIndividualWorkspaces &&
                            !editingWorkSeatsIsDisabled
                          )
                            setRoundingSettingsPopupAnchorEl(e.currentTarget);
                        }}
                        fontSize="small"
                      />
                    </EditRoundingSettingsContainer>
                  ) : null}
                </WorkSeatsHoverArea>
              </PaddedNoOverflow>
            </HeaderRow>
            <Subtitles
              className="nowrap"
              style={{ height: 32 }}
              data-testid={`worksheet_category_row_label1_${testingSuffix}`}
            >
              Total {secondaryTitle} Seats <br />
              {!isWholeNumber(Number(totalWorkSeats)) &&
                categoryIsIndividualWorkspaces && (
                  <span className="error">Seats should be a whole number</span>
                )}
            </Subtitles>
            {categoryIsIndividualWorkspaces && (
              <Popover
                onClose={() => setRoundingSettingsPopupAnchorEl(null)}
                open={Boolean(roundingSettingsPopupAnchorEl)}
                anchorEl={roundingSettingsPopupAnchorEl}
                disableScrollLock
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                transformOrigin={{ horizontal: 18, vertical: 0 }}
              >
                <WorkSeatsRoundingSettingsModalContainer>
                  <Box
                    data-testid="original-work-seats"
                    className="work-seats"
                    mb={0.5}
                  >
                    {numberWithCommas(
                      currentScenario.workSeatsOriginal,
                      true,
                      true
                    )}
                  </Box>

                  {currentScenario.workSeatsRoundingSetting !==
                  WorkSeatsRoundingSetting.RoundUp ? (
                    <MenuItem
                      onClick={() =>
                        updateScenarioWorkSeatsRoundingSetting(
                          WorkSeatsRoundingSetting.RoundUp
                        )
                      }
                      sx={{ paddingLeft: 1.5, paddingRight: 1.5 }}
                    >
                      <Row alignItems="center">
                        <ArrowUpward
                          htmlColor={colors.mainBlue}
                          fontSize="small"
                        />
                        <Box ml={0.5}>Round Up</Box>
                      </Row>
                    </MenuItem>
                  ) : null}
                  {currentScenario.workSeatsRoundingSetting !==
                  WorkSeatsRoundingSetting.RoundDown ? (
                    <MenuItem
                      onClick={() =>
                        updateScenarioWorkSeatsRoundingSetting(
                          WorkSeatsRoundingSetting.RoundDown
                        )
                      }
                      sx={{ paddingLeft: 1.5, paddingRight: 1.5 }}
                    >
                      <Row alignItems="center">
                        <ArrowDownward
                          htmlColor={colors.mainBlue}
                          fontSize="small"
                        />
                        <Box ml={0.5}>Round Down</Box>
                      </Row>
                    </MenuItem>
                  ) : null}
                  {currentScenario.workSeatsRoundingSetting !== null &&
                    restoreRoundingSettingComponent}
                </WorkSeatsRoundingSettingsModalContainer>
              </Popover>
            )}
          </AlignEndButtonColumn>
        </GridColumn>
        <GridColumn xs={2}>
          {/*  This is a stupid solution to give somewhere focus after closing the dropdown higher in this component */}
          {/*  DONT DELETE */}
          <input
            style={{ width: '0px', opacity: '0', height: '0', padding: '0' }}
            ref={dropdownRef}
          />
        </GridColumn>
        <GridColumn xs={3}>
          <div
            className={`space-allocation-wrapper ${
              !isExpandedScenario ? 'shaded' : ''
            }`}
          >
            {!isExpandedScenario &&
            isIWCategoryOrSubcategory(projectSpaceTypeCategory) ? (
              <FullWidthJustifyEndRow>
                <ProgramSubtotal
                  data-testid={`worksheet_category_row_value2_${testingSuffix}`}
                >
                  <HeaderRow>
                    <PaddedNoOverflow>{displaySpaceAlloc}%</PaddedNoOverflow>
                  </HeaderRow>
                  <Subtitles
                    data-testid={`worksheet_category_row_label2_${testingSuffix}`}
                    style={{ height: 32 }}
                  >
                    Space Allocation
                  </Subtitles>
                </ProgramSubtotal>
              </FullWidthJustifyEndRow>
            ) : (
              <FullWidthJustifyEndRow>
                <ProgramSubtotal>
                  <HeaderRow
                    data-testid={`worksheet_category_row_value2_${testingSuffix}`}
                  >
                    <PaddedNoOverflow>{percentProgSubUsf}</PaddedNoOverflow>
                  </HeaderRow>
                  <Subtitles
                    style={{ height: 32 }}
                    data-testid={`worksheet_category_row_label2_${testingSuffix}`}
                  >
                    % of Subtotal Program {getUnit('area')}
                  </Subtitles>
                </ProgramSubtotal>
              </FullWidthJustifyEndRow>
            )}
          </div>
        </GridColumn>
        <GridColumn xs={1} className="row-end">
          <FullWidthJustifyEndRow>
            <ProgramSubtotal>
              <HeaderRow
                data-testid={`worksheet_category_row_value3_${testingSuffix}`}
              >
                <PaddedNoOverflow>
                  {convertToUnit(usf, {
                    unitType: 'area',
                    formatter: (v) => numberWithCommas(v, showDecimals, true),
                  })}
                </PaddedNoOverflow>
              </HeaderRow>
              <Subtitles style={{ height: 32 }}>
                <NoOverflow
                  data-testid={`worksheet_category_row_label3_${testingSuffix}`}
                >
                  Subtotal {getUnit('area')}
                </NoOverflow>
              </Subtitles>
            </ProgramSubtotal>
          </FullWidthJustifyEndRow>
        </GridColumn>
      </GridRow>
    </>
  );
};

export default SpaceTypeCategoryRow;
